<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import AdminBrandForm from '@/views/forms/AdminBrandForm.vue'
import AdminPlatformAdvertiserForm from '@/views/forms/AdminPlatformAdvertiserForm.vue'
import { useAdmin } from '@/stores/admin'
import { useImports } from '@/stores/imports'
import type { Brand, PlatformAdvertiser } from '@/stores/admin'
import type { AdvertiserImportStats } from '@/stores/imports'
import type { ImportJobStatus } from '@/lib/utils'
import { handleError } from '@/lib/error'
import {
  formatPlatformName,
  buildAdLibraryUrl,
  getImportStatusColor,
  formatRelativeTime,
} from '@/lib/utils'

const props = defineProps<{
  brand: Brand | null
}>()

const emit = defineEmits<{
  close: []
  deleted: []
  updated: [brand: Brand]
}>()

const admin = useAdmin()
const imports = useImports()
const router = useRouter()

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function openImportDetail(jobId: string | null) {
  if (jobId && UUID_RE.test(jobId)) {
    router.push({ path: '/admin/imports', hash: `#job-${jobId}` }).then(() => emit('close'))
  }
}

// Loading state
const isLoadingAdvertisers = ref(false)

// Advertiser form
const showAdvertiserForm = ref(false)
const selectedGroupedAdvertiser = ref<GroupedAdvertiser | null>(null)
const brandAdvertisers = ref<PlatformAdvertiser[]>([])

// Edit brand state
const isEditingBrand = ref(false)
const isImportingMetaLogo = ref(false)
const isTogglingSector = ref(false)
const showDeleteConfirm = ref(false)

// Collapsible sections (default closed)
const showAdvertisersSection = ref(false)
const showSectorsSection = ref(false)
const showImportsSection = ref(false)
const expandedPlatforms = ref<Set<string>>(new Set())

// Imports state
const advertiserImportStats = ref<AdvertiserImportStats[]>([])

// Track request ID to prevent stale data on rapid brand switching
let brandLoadRequestId = 0

// Type for grouped advertiser display
type GroupedAdvertiser = {
  platform_id: string
  platform_code: string
  platform_name: string
  platform_advertiser_identifier: string
  countries: { code: string | null; name: string | null; ids: string[] }[]
  extra_kwargs: Record<string, unknown> | null
  search_type?: 'keyword' | 'advertiser_name'
}

// Ad library URL helpers

// Resolve the extra_kwargs key holding the filter IDs/names for a platform.
// Prefers the platform-config column `biz_id_extra_kwargs_field` (set by the
// alembic migration), falls back to a hardcoded per-platform mapping so the
// UI keeps working in envs where the migration has not yet been applied
// (form saves don't depend on the column, so extra_kwargs can exist before
// the column value is populated).
const FALLBACK_EXTRA_KWARGS_KEY: Record<string, string> = {
  TIKTOK: 'relevant_adv_biz_ids',
  SNAPCHAT: 'relevant_profile_names',
}

function getBizIdKey(adv: GroupedAdvertiser): string | null {
  const platform = admin.platforms.find((p) => p.id === adv.platform_id)
  return (
    platform?.biz_id_extra_kwargs_field ?? FALLBACK_EXTRA_KWARGS_KEY[platform?.code ?? ''] ?? null
  )
}

function hasBizIds(adv: GroupedAdvertiser): boolean {
  const key = getBizIdKey(adv)
  if (!key) return false
  const bizIds = adv.extra_kwargs?.[key]
  return Array.isArray(bizIds) && bizIds.length > 0
}

function getBizIds(adv: GroupedAdvertiser): (string | null)[] {
  const key = getBizIdKey(adv)
  if (!key) return []
  const bizIds = adv.extra_kwargs?.[key]
  return Array.isArray(bizIds) ? bizIds : []
}

function getAdLibraryUrl(adv: GroupedAdvertiser, bizId?: string | null): string | null {
  const platform = admin.platforms.find((p) => p.id === adv.platform_id)
  if (!platform?.ad_library_url_template) return null

  // For advertiser_name, always use the first (and only) bizId
  let effectiveBizId = bizId ?? undefined
  if (!effectiveBizId && adv.search_type === 'advertiser_name') {
    const bizIds = getBizIds(adv)
    effectiveBizId = bizIds[0] ?? undefined
  }

  return buildAdLibraryUrl(platform.ad_library_url_template, adv.platform_advertiser_identifier, {
    bizId: effectiveBizId,
    bizIdPlaceholder: platform.biz_id_extra_kwargs_field ?? undefined,
    requiresDateRange: platform.ad_library_requires_date_range,
    extraParams: platform.ad_library_extra_params ?? undefined,
    searchType: adv.search_type,
  })
}

// Group advertisers by platform and advertiser_id
const groupedAdvertisersByPlatform = computed(() => {
  const byPlatform: Record<string, Record<string, GroupedAdvertiser>> = {}

  for (const adv of brandAdvertisers.value) {
    if (!byPlatform[adv.platform_name]) {
      byPlatform[adv.platform_name] = {}
    }

    // Include search_type in key to avoid merging different search types
    const key = `${adv.platform_advertiser_identifier}:${adv.search_type ?? 'keyword'}`
    if (!byPlatform[adv.platform_name][key]) {
      byPlatform[adv.platform_name][key] = {
        platform_id: adv.platform_id,
        platform_code: adv.platform_code,
        platform_name: adv.platform_name,
        platform_advertiser_identifier: adv.platform_advertiser_identifier,
        countries: [],
        extra_kwargs: adv.extra_kwargs,
        search_type: adv.search_type,
      }
    }

    byPlatform[adv.platform_name][key].countries.push({
      code: adv.country_code,
      name: adv.country_name,
      ids: [adv.id],
    })
  }

  // Convert to array format and sort
  const result: Record<string, GroupedAdvertiser[]> = {}
  for (const [platformName, advertisers] of Object.entries(byPlatform)) {
    const advList = Object.values(advertisers)
    for (const adv of advList) {
      adv.countries.sort((a, b) => {
        if (a.code === null) return -1
        if (b.code === null) return 1
        return (a.name || a.code || '').localeCompare(b.name || b.code || '')
      })
    }
    advList.sort((a, b) => {
      const aHasGlobal = a.countries.some((c) => c.code === null)
      const bHasGlobal = b.countries.some((c) => c.code === null)
      if (aHasGlobal && !bHasGlobal) return -1
      if (!aHasGlobal && bHasGlobal) return 1
      return 0
    })
    result[platformName] = advList
  }
  return result
})

// Check if brand has Meta advertiser (for logo import)
const hasMetaAdvertiser = computed(() => {
  return brandAdvertisers.value.some((adv) => adv.platform_code === 'META')
})

// Computed Map for O(1) import stats lookup by platform|advertiserId|countryCode
const importStatsMap = computed(() => {
  const map = new Map<string, AdvertiserImportStats>()
  for (const stat of advertiserImportStats.value) {
    const key = `${stat.platformCode}|${stat.platformAdvertiserIdentifier}|${stat.countryCode ?? ''}`
    map.set(key, stat)
  }
  return map
})

// Derive platform-level import stats from per-advertiser stats
type PlatformImportSummary = {
  platform: string
  status: ImportJobStatus
  completedTs: string | null
  numAds: number
  jobId: string | null
}

const platformImportStats = computed((): PlatformImportSummary[] => {
  const byPlatform = new Map<
    string,
    { status: ImportJobStatus; completedTs: string | null; numAds: number; jobId: string | null }
  >()

  for (const stat of advertiserImportStats.value) {
    const existing = byPlatform.get(stat.platformCode)
    // Keep the most recent status per platform, sum ads
    if (
      !existing ||
      (stat.completedTs && (!existing.completedTs || stat.completedTs > existing.completedTs))
    ) {
      byPlatform.set(stat.platformCode, {
        status: stat.status,
        completedTs: stat.completedTs,
        numAds: (existing?.numAds ?? 0) + (stat.numAds ?? 0),
        jobId: stat.jobId,
      })
    } else if (existing) {
      existing.numAds += stat.numAds ?? 0
    }
  }

  return Array.from(byPlatform.entries()).map(([platform, data]) => ({
    platform,
    ...data,
  }))
})

// Get import stats for a specific advertiser/country combination (O(1) lookup)
function getAdvertiserImportStats(
  platformCode: string,
  advertiserId: string,
  countryCode: string | null,
): AdvertiserImportStats | null {
  const key = `${platformCode}|${advertiserId}|${countryCode ?? ''}`
  return importStatsMap.value.get(key) ?? null
}

// Load advertisers and import stats when brand changes
watch(
  () => props.brand,
  async (brand) => {
    const requestId = ++brandLoadRequestId
    // Reset UI state immediately (synchronously, before any await)
    showAdvertiserForm.value = false
    isEditingBrand.value = false
    showImportsSection.value = false

    if (brand) {
      isLoadingAdvertisers.value = true
      try {
        const [advertisers, importStats] = await Promise.all([
          admin.fetchBrandAdvertisers(brand.id),
          imports.fetchBrandAdvertiserImportStats(brand.id).catch((err) => {
            console.error('Failed to fetch advertiser import stats:', err)
            return []
          }),
        ])
        // Discard stale response if brand changed during fetch
        if (requestId !== brandLoadRequestId) return
        brandAdvertisers.value = advertisers
        advertiserImportStats.value = importStats
      } catch (err) {
        if (requestId !== brandLoadRequestId) return
        handleError(err, 'Failed to load advertisers')
      } finally {
        if (requestId === brandLoadRequestId) {
          isLoadingAdvertisers.value = false
        }
      }
    } else {
      brandAdvertisers.value = []
      advertiserImportStats.value = []
    }
  },
  { immediate: true },
)

// Format website URL for display (remove http://www.)
function formatWebsiteDisplay(url: string | null): string {
  if (!url) return ''
  return url.replace(/^https?:\/\/(www\.)?/, '')
}

// Get full URL for linking (validates scheme to prevent XSS)
function getWebsiteUrl(url: string | null): string {
  if (!url) return ''
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return ''
    return parsed.href
  } catch {
    return ''
  }
}

// Brand actions
async function handleUpdateBrand(data: {
  name: string
  website?: string | null
  logo_file?: File
  delete_logo?: boolean
}) {
  if (!props.brand) return
  const brandId = props.brand.id
  try {
    await admin.updateBrand(brandId, { name: data.name, website: data.website })
    if (data.logo_file) {
      await admin.saveBrandLogo(brandId, data.logo_file)
    } else if (data.delete_logo) {
      await admin.deleteBrandLogo(brandId)
    }
    isEditingBrand.value = false
    const updatedBrand = admin.brands.find((b) => b.id === brandId)
    if (updatedBrand) {
      emit('updated', updatedBrand)
    }
  } catch (err) {
    handleError(err, 'Failed to update brand')
  }
}

async function handleDeleteBrand() {
  if (!props.brand) return
  try {
    await admin.deleteBrand(props.brand.id)
    showDeleteConfirm.value = false
    emit('deleted')
  } catch (err) {
    handleError(err, 'Failed to delete brand')
  }
}

async function handleImportMetaLogo() {
  if (!props.brand) return
  const brandId = props.brand.id
  isImportingMetaLogo.value = true
  try {
    await admin.importMetaLogo(brandId)
    const updatedBrand = admin.brands.find((b) => b.id === brandId)
    if (updatedBrand) {
      emit('updated', updatedBrand)
    }
  } catch (err) {
    handleError(err, 'Failed to import logo from Meta')
  } finally {
    isImportingMetaLogo.value = false
  }
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
}

// Advertiser actions
function openCreateAdvertiserForm() {
  selectedGroupedAdvertiser.value = null
  showAdvertiserForm.value = true
}

function openEditGroupedAdvertiser(adv: GroupedAdvertiser) {
  selectedGroupedAdvertiser.value = adv
  showAdvertiserForm.value = true
}

function closeAdvertiserForm() {
  selectedGroupedAdvertiser.value = null
  showAdvertiserForm.value = false
}

async function handleSaveAdvertiser(data: {
  platform_id?: string
  platform_advertiser_identifier: string
  country_codes: (string | null)[]
  extra_kwargs?: Record<string, unknown> | null
  search_type?: 'keyword' | 'advertiser_name'
}) {
  if (!props.brand) return

  const brandId = props.brand.id
  const groupedAdv = selectedGroupedAdvertiser.value

  try {
    if (groupedAdv) {
      const existingCodes = groupedAdv.countries.map((c) => c.code)
      const newCodes = data.country_codes

      // Delete removed countries
      const deletePromises: Promise<void>[] = []
      for (const country of groupedAdv.countries) {
        if (!newCodes.includes(country.code)) {
          for (const id of country.ids) {
            deletePromises.push(admin.deleteBrandAdvertiser(brandId, id))
          }
        }
      }
      await Promise.all(deletePromises)

      // Add new countries, carrying over the group's sectors (rows are recreated, links are not)
      const groupIds = new Set(groupedAdv.countries.flatMap((c) => c.ids))
      const groupAdvertisers = brandAdvertisers.value.filter((a) => groupIds.has(a.id))
      const sectorIds = [
        ...new Set(
          groupAdvertisers.flatMap((a) =>
            a.sectors.filter((s) => s.source === 'advertiser').map((s) => s.id),
          ),
        ),
      ]
      // Collect excluded sectors: brand sectors not present in any group advertiser
      const brandSectorIds = new Set(props.brand.sectors.map((s) => s.id) ?? [])
      const excludedSectorIds = [
        ...new Set(
          groupAdvertisers.flatMap((a) => {
            const advSectorIds = new Set(a.sectors.map((s) => s.id))
            return [...brandSectorIds].filter((id) => !advSectorIds.has(id))
          }),
        ),
      ]
      const createPromises = newCodes
        .filter((code) => !existingCodes.includes(code))
        .map((code) =>
          admin.createBrandAdvertiser(brandId, {
            platform_id: groupedAdv.platform_id,
            platform_advertiser_identifier: data.platform_advertiser_identifier,
            country_code: code,
            extra_kwargs: data.extra_kwargs,
            search_type: data.search_type,
          }),
        )
      const created = await Promise.all(createPromises)
      await Promise.all([
        ...created.flatMap((adv) =>
          sectorIds.map((sid) => admin.addAdvertiserSector(brandId, adv.id, sid)),
        ),
        ...created.flatMap((adv) =>
          excludedSectorIds.map((sid) => admin.excludeAdvertiserSector(brandId, adv.id, sid)),
        ),
      ])

      // Update remaining records
      const updatePromises: Promise<unknown>[] = []
      for (const country of groupedAdv.countries) {
        if (newCodes.includes(country.code)) {
          for (const id of country.ids) {
            updatePromises.push(
              admin.updateBrandAdvertiser(brandId, id, {
                platform_advertiser_identifier: data.platform_advertiser_identifier,
                extra_kwargs: data.extra_kwargs,
                search_type: data.search_type,
              }),
            )
          }
        }
      }
      await Promise.all(updatePromises)
    } else {
      // Create new
      await Promise.all(
        data.country_codes.map((countryCode) =>
          admin.createBrandAdvertiser(brandId, {
            platform_id: data.platform_id!,
            platform_advertiser_identifier: data.platform_advertiser_identifier,
            country_code: countryCode,
            extra_kwargs: data.extra_kwargs,
            search_type: data.search_type,
          }),
        ),
      )
    }

    // Refresh advertisers and import stats
    const [advertisers, importStats] = await Promise.all([
      admin.fetchBrandAdvertisers(brandId),
      imports.fetchBrandAdvertiserImportStats(brandId).catch((err) => {
        console.error('Failed to refresh import stats:', err)
        return advertiserImportStats.value
      }),
    ])
    brandAdvertisers.value = advertisers
    advertiserImportStats.value = importStats
    await admin.fetchBrands()
    const updatedBrand = admin.brands.find((b) => b.id === brandId)
    if (updatedBrand) {
      emit('updated', updatedBrand)
    }
    selectedGroupedAdvertiser.value = null
    showAdvertiserForm.value = false
  } catch (err) {
    handleError(err, 'Failed to save advertiser')
  }
}

async function handleDeleteGroupedAdvertiser(adv: GroupedAdvertiser) {
  if (!props.brand) return
  const brandId = props.brand.id

  try {
    const deletePromises = adv.countries.flatMap((country) =>
      country.ids.map((id) => admin.deleteBrandAdvertiser(brandId, id)),
    )
    await Promise.all(deletePromises)
    // Refresh advertisers and import stats
    const [advertisers, importStats] = await Promise.all([
      admin.fetchBrandAdvertisers(brandId),
      imports.fetchBrandAdvertiserImportStats(brandId).catch((err) => {
        console.error('Failed to refresh import stats:', err)
        return advertiserImportStats.value
      }),
    ])
    brandAdvertisers.value = advertisers
    advertiserImportStats.value = importStats
    await admin.fetchBrands()
    const updatedBrand = admin.brands.find((b) => b.id === brandId)
    if (updatedBrand) {
      emit('updated', updatedBrand)
    }
  } catch (err) {
    handleError(err, 'Failed to delete advertiser')
  }
}

// Get sectors aggregated from all underlying advertisers in a grouped row
function getGroupSectors(adv: GroupedAdvertiser) {
  const ids = new Set(adv.countries.flatMap((c) => c.ids))
  const map = new Map<string, (typeof brandAdvertisers.value)[0]['sectors'][0]>()
  for (const a of brandAdvertisers.value) {
    if (ids.has(a.id)) {
      for (const s of a.sectors) map.set(s.id, s)
    }
  }
  return [...map.values()]
}

// Check if sectors diverge between advertisers in a group
function hasGroupSectorDivergence(adv: GroupedAdvertiser): boolean {
  const ids = new Set(adv.countries.flatMap((c) => c.ids))
  const advertisersInGroup = brandAdvertisers.value.filter((a) => ids.has(a.id))
  if (advertisersInGroup.length <= 1) return false
  // Compare sector IDs between advertisers
  const firstSectorIds = new Set(advertisersInGroup[0].sectors.map((s) => s.id))
  return advertisersInGroup.slice(1).some((a) => {
    const sectorIds = new Set(a.sectors.map((s) => s.id))
    if (sectorIds.size !== firstSectorIds.size) return true
    for (const id of sectorIds) {
      if (!firstSectorIds.has(id)) return true
    }
    return false
  })
}

// Platform collapse toggle
function togglePlatform(platformName: string) {
  const newSet = new Set(expandedPlatforms.value)
  if (newSet.has(platformName)) {
    // Don't collapse if editing an advertiser in this platform
    if (
      showAdvertiserForm.value &&
      selectedGroupedAdvertiser.value?.platform_name === platformName
    ) {
      return
    }
    newSet.delete(platformName)
  } else {
    newSet.add(platformName)
  }
  expandedPlatforms.value = newSet
}

// Sector management
async function toggleSector(sectorId: string) {
  if (!props.brand || isTogglingSector.value) return
  isTogglingSector.value = true
  const brandId = props.brand.id
  const hasSector = props.brand.sectors.some((s) => s.id === sectorId)

  try {
    if (hasSector) {
      await admin.removeBrandSector(brandId, sectorId)
    } else {
      await admin.addBrandSector(brandId, sectorId)
    }
    await admin.fetchSectors() // Refresh to keep brand_count accurate
    const updatedBrand = admin.brands.find((b) => b.id === brandId)
    if (updatedBrand) {
      emit('updated', updatedBrand)
    }
  } catch (err) {
    handleError(err, 'Failed to update sector')
  } finally {
    isTogglingSector.value = false
  }
}
</script>

<template>
  <div class="p-6 space-y-6 h-full overflow-y-auto" v-if="brand">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <UAvatar :src="brand.logo_url ?? undefined" :alt="brand.name" size="lg" />
        <div>
          <h2 class="text-xl font-semibold">{{ brand.name }}</h2>
          <p class="text-sm text-muted">{{ brand.slug }}</p>
          <a
            v-if="brand.website && getWebsiteUrl(brand.website)"
            :href="getWebsiteUrl(brand.website)"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            {{ formatWebsiteDisplay(brand.website) }}
            <UIcon name="i-lucide-external-link" class="w-3 h-3" />
          </a>
        </div>
      </div>
      <UButton icon="i-lucide-x" color="neutral" variant="ghost" @click="emit('close')" />
    </div>

    <!-- Brand Actions -->
    <div class="flex gap-2 flex-wrap">
      <UButton icon="i-lucide-pencil" color="neutral" variant="soft" @click="isEditingBrand = true">
        Edit Brand
      </UButton>
      <UButton
        v-if="!brand.logo_url"
        icon="i-lucide-download"
        color="info"
        variant="soft"
        :loading="isImportingMetaLogo || isLoadingAdvertisers"
        :disabled="isLoadingAdvertisers || !hasMetaAdvertiser"
        @click="handleImportMetaLogo"
      >
        Import logo from Meta
      </UButton>
      <UButton
        icon="i-lucide-trash-2"
        color="error"
        variant="soft"
        @click="showDeleteConfirm = true"
      >
        Delete Brand
      </UButton>
    </div>

    <!-- Edit Brand Form -->
    <UCard v-if="isEditingBrand">
      <template #header>
        <h3 class="text-lg font-semibold">Edit Brand</h3>
      </template>
      <AdminBrandForm :brand="brand" @submit="handleUpdateBrand" @cancel="isEditingBrand = false" />
    </UCard>

    <!-- Platform Advertisers -->
    <div class="space-y-4">
      <div
        class="flex items-center justify-between cursor-pointer"
        @click="showAdvertisersSection = !showAdvertisersSection"
      >
        <div class="flex items-center gap-2">
          <UIcon
            :name="showAdvertisersSection ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
            class="text-muted"
          />
          <h3 class="text-lg font-semibold">Platform Advertisers</h3>
          <UBadge color="neutral" variant="subtle" size="sm">
            {{ brandAdvertisers.length }}
          </UBadge>
        </div>
        <UButton
          v-if="!showAdvertiserForm && showAdvertisersSection"
          icon="i-lucide-plus"
          color="primary"
          size="sm"
          @click.stop="openCreateAdvertiserForm"
        >
          Add
        </UButton>
      </div>

      <template v-if="showAdvertisersSection">
        <!-- Inline Add Form (only for creating new) -->
        <UCard v-if="showAdvertiserForm && !selectedGroupedAdvertiser">
          <template #header>
            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-semibold">Add Platform Advertiser</h4>
              </div>
              <UButton
                icon="i-lucide-x"
                color="neutral"
                variant="ghost"
                size="xs"
                @click="showAdvertiserForm = false"
              />
            </div>
          </template>
          <AdminPlatformAdvertiserForm
            :advertiser="null"
            :platforms="admin.platforms"
            :countries="admin.countries"
            @submit="handleSaveAdvertiser"
            @cancel="showAdvertiserForm = false"
          />
        </UCard>

        <div v-if="isLoadingAdvertisers" class="flex justify-center py-8">
          <UIcon name="i-lucide-loader-2" class="animate-spin text-2xl text-muted" />
        </div>

        <div v-else-if="brandAdvertisers.length === 0" class="text-center py-8 text-muted">
          No platform advertisers configured
        </div>

        <div v-else class="space-y-4">
          <div
            v-for="(advertisers, platformName) in groupedAdvertisersByPlatform"
            :key="platformName"
            class="space-y-2"
          >
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="togglePlatform(platformName as string)"
            >
              <UIcon
                :name="
                  expandedPlatforms.has(platformName as string)
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
                class="text-muted"
              />
              <UBadge color="neutral" variant="outline" size="sm">
                {{ formatPlatformName(platformName) }}
              </UBadge>
              <span class="text-sm text-muted">{{ advertisers.length }}</span>
            </div>
            <div v-if="expandedPlatforms.has(platformName as string)" class="space-y-2">
              <template v-for="adv in advertisers" :key="adv.platform_advertiser_identifier">
                <!-- Inline Edit Form (replaces item being edited) -->
                <UCard
                  v-if="
                    showAdvertiserForm &&
                    selectedGroupedAdvertiser?.platform_advertiser_identifier ===
                      adv.platform_advertiser_identifier &&
                    selectedGroupedAdvertiser?.platform_id === adv.platform_id
                  "
                  class="border-primary"
                >
                  <template #header>
                    <div class="flex items-center justify-between">
                      <h4 class="font-semibold">Edit Advertiser</h4>
                      <UButton
                        icon="i-lucide-x"
                        color="neutral"
                        variant="ghost"
                        size="xs"
                        @click="closeAdvertiserForm"
                      />
                    </div>
                  </template>
                  <AdminPlatformAdvertiserForm
                    :advertiser="selectedGroupedAdvertiser"
                    :platforms="admin.platforms"
                    :countries="admin.countries"
                    @submit="handleSaveAdvertiser"
                    @cancel="closeAdvertiserForm"
                  />
                </UCard>
                <!-- Normal advertiser row -->
                <div v-else class="p-3 rounded-md bg-elevated hover:bg-accented">
                  <div class="flex items-center gap-4">
                    <!-- Left: Countries (fixed width) -->
                    <div class="flex gap-1 flex-wrap w-24 shrink-0">
                      <template v-for="country in adv.countries" :key="country.code ?? 'global'">
                        <UBadge v-if="country.code" color="neutral" variant="subtle" size="md">
                          <span class="text-lg mr-1">{{
                            admin.countries.find((c) => c.code === country.code)?.emoji || ''
                          }}</span>
                          {{ country.name || country.code }}
                        </UBadge>
                        <UBadge v-else color="info" variant="subtle" size="md">
                          <span class="text-lg mr-1">🌐</span>
                          Global
                        </UBadge>
                      </template>
                    </div>
                    <!-- Center: Badge + Advertiser ID -->
                    <div class="flex items-center gap-2 flex-1 overflow-x-auto">
                      <UBadge
                        v-if="adv.platform_code === 'TIKTOK' && adv.search_type"
                        :color="adv.search_type === 'advertiser_name' ? 'success' : 'warning'"
                        variant="subtle"
                        size="xs"
                        class="shrink-0"
                      >
                        {{ adv.search_type === 'advertiser_name' ? 'Adv Name' : 'Keyword' }}
                      </UBadge>
                      <code class="text-sm font-mono text-muted whitespace-nowrap">{{
                        adv.platform_advertiser_identifier
                      }}</code>
                    </div>
                    <UButton
                      icon="i-lucide-copy"
                      color="neutral"
                      variant="ghost"
                      size="xs"
                      title="Copy advertiser ID"
                      @click="copyToClipboard(adv.platform_advertiser_identifier)"
                    />
                    <!-- Right: Actions (fixed) -->
                    <div class="flex items-center gap-1 shrink-0">
                      <UButton
                        v-if="getAdLibraryUrl(adv)"
                        :href="getAdLibraryUrl(adv)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        icon="i-lucide-external-link"
                        color="neutral"
                        variant="ghost"
                        size="xs"
                        title="View in Ad Library"
                      />
                      <UButton
                        icon="i-lucide-tag"
                        color="neutral"
                        variant="ghost"
                        size="xs"
                        title="Manage sectors"
                        :to="
                          adv.countries[0]?.ids[0]
                            ? `/admin/advertisers?search=${encodeURIComponent(adv.platform_advertiser_identifier)}#${adv.countries[0].ids[0]}`
                            : `/admin/advertisers?search=${encodeURIComponent(adv.platform_advertiser_identifier)}`
                        "
                      />
                      <UButton
                        icon="i-lucide-pencil"
                        color="neutral"
                        variant="ghost"
                        size="xs"
                        @click="openEditGroupedAdvertiser(adv)"
                      />
                      <UButton
                        icon="i-lucide-trash-2"
                        color="error"
                        variant="ghost"
                        size="xs"
                        @click="handleDeleteGroupedAdvertiser(adv)"
                      />
                    </div>
                  </div>
                  <!-- Sectors (read-only) -->
                  <div
                    v-if="getGroupSectors(adv).length > 0"
                    class="mt-2 flex flex-wrap gap-1 items-center"
                  >
                    <UBadge
                      v-for="s in getGroupSectors(adv)"
                      :key="s.id"
                      :color="s.source === 'brand' ? 'neutral' : 'primary'"
                      variant="outline"
                      size="xs"
                    >
                      <span v-if="s.emoji" class="mr-0.5">{{ s.emoji }}</span>
                      {{ s.name }}
                    </UBadge>
                    <UTooltip
                      v-if="hasGroupSectorDivergence(adv)"
                      text="Sectors differ between countries"
                    >
                      <UIcon name="i-lucide-alert-triangle" class="text-warning text-sm" />
                    </UTooltip>
                  </div>
                  <!-- Business IDs / Profile names chips + Snapchat null-match indicator -->
                  <div v-if="hasBizIds(adv)" class="mt-2">
                    <div class="flex flex-wrap gap-x-4 gap-y-1">
                      <template v-for="(bizId, idx) in getBizIds(adv)" :key="idx">
                        <span
                          v-if="bizId !== null"
                          class="text-xs text-muted font-mono inline-flex items-center gap-1"
                        >
                          {{ bizId }}
                          <a
                            v-if="getAdLibraryUrl(adv, bizId)"
                            :href="getAdLibraryUrl(adv, bizId)!"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-muted hover:text-default"
                          >
                            <UIcon name="i-lucide-external-link" class="w-3 h-3" />
                          </a>
                        </span>
                      </template>
                    </div>
                    <span
                      v-if="adv.platform_code === 'SNAPCHAT'"
                      class="block mt-1 text-xs italic text-muted"
                    >
                      {{
                        getBizIds(adv).includes(null)
                          ? '+ also matches ads without profile name'
                          : '(strict: ads without profile name are excluded)'
                      }}
                    </span>
                  </div>
                  <!-- Import Stats per country -->
                  <div
                    v-if="advertiserImportStats.length > 0"
                    class="mt-2 flex flex-wrap gap-x-4 gap-y-1"
                  >
                    <template v-for="country in adv.countries" :key="country.code ?? 'global'">
                      <span
                        v-if="
                          getAdvertiserImportStats(
                            adv.platform_code,
                            adv.platform_advertiser_identifier,
                            country.code,
                          )
                        "
                        class="text-xs inline-flex items-center gap-1"
                      >
                        <UBadge
                          :color="
                            getImportStatusColor(
                              getAdvertiserImportStats(
                                adv.platform_code,
                                adv.platform_advertiser_identifier,
                                country.code,
                              )!.status,
                            )
                          "
                          variant="subtle"
                          size="xs"
                        >
                          {{
                            getAdvertiserImportStats(
                              adv.platform_code,
                              adv.platform_advertiser_identifier,
                              country.code,
                            )!.status
                          }}
                        </UBadge>
                        <span class="text-muted">
                          {{
                            formatRelativeTime(
                              getAdvertiserImportStats(
                                adv.platform_code,
                                adv.platform_advertiser_identifier,
                                country.code,
                              )!.completedTs,
                            )
                          }}
                        </span>
                        <span
                          v-if="
                            getAdvertiserImportStats(
                              adv.platform_code,
                              adv.platform_advertiser_identifier,
                              country.code,
                            )!.numAds != null
                          "
                          class="text-muted"
                        >
                          ({{
                            getAdvertiserImportStats(
                              adv.platform_code,
                              adv.platform_advertiser_identifier,
                              country.code,
                            )!.numAds
                          }}
                          ads)
                        </span>
                        <button
                          v-if="
                            getAdvertiserImportStats(
                              adv.platform_code,
                              adv.platform_advertiser_identifier,
                              country.code,
                            )?.jobId
                          "
                          class="text-muted hover:text-default"
                          @click.stop="
                            openImportDetail(
                              getAdvertiserImportStats(
                                adv.platform_code,
                                adv.platform_advertiser_identifier,
                                country.code,
                              )?.jobId ?? null,
                            )
                          "
                        >
                          <UIcon name="i-lucide-external-link" class="w-3 h-3" />
                        </button>
                      </span>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Sectors -->
    <div class="space-y-4">
      <div
        class="flex items-center justify-between cursor-pointer"
        @click="showSectorsSection = !showSectorsSection"
      >
        <div class="flex items-center gap-2">
          <UIcon
            :name="showSectorsSection ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
            class="text-muted"
          />
          <h3 class="text-lg font-semibold">Sectors</h3>
          <UBadge color="neutral" variant="subtle" size="sm">
            {{ brand?.sectors.length || 0 }}
          </UBadge>
        </div>
      </div>

      <template v-if="showSectorsSection">
        <div v-if="admin.sectors.length === 0" class="text-center py-4 text-muted text-sm">
          No sectors available.
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <UButton
            v-for="sector in admin.sectors"
            :key="sector.id"
            :color="brand?.sectors.some((s) => s.id === sector.id) ? 'primary' : 'neutral'"
            :variant="brand?.sectors.some((s) => s.id === sector.id) ? 'solid' : 'soft'"
            size="xs"
            @click="toggleSector(sector.id)"
          >
            <span v-if="sector.emoji" class="mr-1">{{ sector.emoji }}</span>
            {{ sector.name }}
          </UButton>
        </div>
      </template>
    </div>

    <!-- Imports -->
    <div class="space-y-4">
      <div
        class="flex items-center justify-between cursor-pointer"
        @click="showImportsSection = !showImportsSection"
      >
        <div class="flex items-center gap-2">
          <UIcon
            :name="showImportsSection ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
            class="text-muted"
          />
          <h3 class="text-lg font-semibold">Imports</h3>
          <UBadge v-if="platformImportStats.length > 0" color="neutral" variant="subtle" size="sm">
            {{ platformImportStats.length }}
          </UBadge>
        </div>
      </div>

      <template v-if="showImportsSection">
        <div v-if="platformImportStats.length === 0" class="text-center py-4 text-muted text-sm">
          No imports found.
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="summary in platformImportStats"
            :key="summary.platform"
            class="p-2 rounded bg-elevated"
          >
            <div class="grid grid-cols-[6rem_auto_5rem_5rem_1.5rem] items-center gap-2 text-sm">
              <span class="font-medium">{{ summary.platform }}</span>
              <UBadge
                :color="getImportStatusColor(summary.status)"
                size="sm"
                class="justify-self-start"
              >
                {{ summary.status }}
              </UBadge>
              <span class="text-muted text-right">{{
                formatRelativeTime(summary.completedTs)
              }}</span>
              <span v-if="summary.numAds != null" class="text-right">{{ summary.numAds }} ads</span>
              <span v-else class="text-muted text-right">—</span>
              <UButton
                v-if="summary.jobId"
                icon="i-lucide-external-link"
                color="neutral"
                variant="ghost"
                size="xs"
                @click="openImportDetail(summary.jobId)"
              />
            </div>
          </div>

          <UButton
            variant="soft"
            size="sm"
            icon="i-lucide-external-link"
            :to="`/admin/imports?search=${encodeURIComponent(brand?.name || '')}`"
          >
            View all imports
          </UButton>
        </div>
      </template>
    </div>

    <!-- Delete Confirmation Modal -->
    <UModal v-model:open="showDeleteConfirm">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Delete Brand</h3>
          </template>
          <p>
            Are you sure you want to delete <strong>{{ brand.name }}</strong
            >? This action cannot be undone.
          </p>
          <template #footer>
            <div class="flex justify-end gap-2">
              <UButton color="neutral" variant="soft" @click="showDeleteConfirm = false">
                Cancel
              </UButton>
              <UButton color="error" @click="handleDeleteBrand"> Delete </UButton>
            </div>
          </template>
        </UCard>
      </template>
    </UModal>
  </div>
</template>
